import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap';
import MedDataTable from '../common/MedDataTable';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import AnalysisSegmentCharts from './AnalysisSegmentCharts';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { getAuthHeaders } from '../../helpers/utils';

const FACULTIES_ENDPOINT = `${apiBase}/p-admin/kasb.php?action=faculties`;
const GROUP_ENDPOINT = `${apiBase}/p-admin/kasb.php?action=group`;

const DEFAULT_UNRATED = {
  label: 'Qiymatlanmagan',
  title: 'Qiymatlanmagan',
  summary: 'Talaba bo‘yicha kasb testi natijasi hali kiritilmagan.',
  tone: 'secondary'
};

const readGroupId = search => {
  const params = new URLSearchParams(search || '');
  return (params.get('guruh_id') || '').trim();
};

const getFacultyKey = faculty => faculty.code || faculty.name;

const sortByDisplayOrder = items =>
  [...(items || [])].sort((left, right) => Number(left.display_order || 99) - Number(right.display_order || 99));

const buildProfileMap = items => {
  const map = {};

  sortByDisplayOrder(items).forEach(item => {
    map[Number(item.natija)] = {
      ...item,
      count: Number(item.count || 0),
      percent: Number(item.percent || 0)
    };
  });

  return map;
};

const resultBadgeClass = tone => `psi-suicide-result psi-suicide-result--${tone || 'secondary'}`;

const KasbAnalysis = ({ history, location }) => {
  const [faculties, setFaculties] = useState([]);
  const [segments, setSegments] = useState({ courses: [], nationalities: [] });
  const [profiles, setProfiles] = useState([]);
  const [unratedMeta, setUnratedMeta] = useState(DEFAULT_UNRATED);
  const [groupData, setGroupData] = useState(null);
  const [loadingFaculties, setLoadingFaculties] = useState(true);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [banner, setBanner] = useState(null);
  const [notFoundMessage, setNotFoundMessage] = useState('');
  const [activePeriodLabel, setActivePeriodLabel] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [expandedFaculties, setExpandedFaculties] = useState({});
  const [activeProfile, setActiveProfile] = useState(null);
  const [pageSize, setPageSize] = useStoredTablePageSize();

  const groupId = readGroupId(location.search);

  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  const changeGroup = groupName => {
    const search = groupName ? `?guruh_id=${encodeURIComponent(groupName)}` : '';
    history.push({
      pathname: '/kasb-testi',
      search
    });
  };

  useEffect(() => {
    let active = true;

    const fetchFaculties = async () => {
      setLoadingFaculties(true);
      setBanner(null);

      try {
        const response = await axios.get(FACULTIES_ENDPOINT, requestConfig);

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setFaculties(response.data.faculties || []);
          setSegments(response.data.segments || { courses: [], nationalities: [] });
          setProfiles(sortByDisplayOrder(response.data.profiles || []));
          setUnratedMeta(response.data.unrated || DEFAULT_UNRATED);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Kasb testi ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Kasb testi ma`lumotlarini yuklashda xatolik yuz berdi.';

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingFaculties(false);
        }
      }
    };

    fetchFaculties();

    return () => {
      active = false;
    };
  }, [requestConfig]);

  useEffect(() => {
    let active = true;

    if (!groupId) {
      setGroupData(null);
      setLoadingGroup(false);
      setNotFoundMessage('');
      return undefined;
    }

    const fetchGroup = async () => {
      setLoadingGroup(true);
      setBanner(null);
      setNotFoundMessage('');

      try {
        const response = await axios.get(GROUP_ENDPOINT, {
          ...requestConfig,
          params: {
            guruh_id: groupId
          }
        });

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setGroupData(response.data.data || null);
          setProfiles(sortByDisplayOrder(response.data.profiles || []));
          setUnratedMeta(response.data.unrated || DEFAULT_UNRATED);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Guruh ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const status = error.response && error.response.status;
        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Guruh ma`lumotlarini yuklashda xatolik yuz berdi.';

        setGroupData(null);

        if (status === 404) {
          setNotFoundMessage(message);
          return;
        }

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingGroup(false);
        }
      }
    };

    fetchGroup();

    return () => {
      active = false;
    };
  }, [groupId, requestConfig]);

  const filteredFaculties = useMemo(() => {
    const search = groupSearch.trim().toLowerCase();

    return faculties
      .map(faculty => {
        const facultyName = (faculty.name || '').toLowerCase();
        const matchesFaculty = facultyName.includes(search);
        const groups = (faculty.groups || []).filter(group => {
          if (!search || matchesFaculty) {
            return true;
          }

          return (group.name || '').toLowerCase().includes(search);
        });

        return {
          ...faculty,
          groups
        };
      })
      .filter(faculty => faculty.groups.length > 0 || (faculty.name || '').toLowerCase().includes(search));
  }, [faculties, groupSearch]);

  useEffect(() => {
    if (!filteredFaculties.length) {
      return;
    }

    setExpandedFaculties(prev => {
      const next = { ...prev };

      if (groupId) {
        const selectedFaculty = filteredFaculties.find(faculty =>
          (faculty.groups || []).some(group => group.name === groupId)
        );

        if (selectedFaculty) {
          next[getFacultyKey(selectedFaculty)] = true;
          return next;
        }
      }

      if (!Object.values(next).some(Boolean)) {
        next[getFacultyKey(filteredFaculties[0])] = true;
      }

      return next;
    });
  }, [filteredFaculties, groupId]);

  const toggleFaculty = facultyKey => {
    setExpandedFaculties(prev => ({
      ...prev,
      [facultyKey]: !prev[facultyKey]
    }));
  };

  const overviewStats = useMemo(() => {
    const aggregatedMap = buildProfileMap(
      profiles.map(profile => ({
        ...profile,
        count: 0,
        percent: 0
      }))
    );

    faculties.forEach(faculty => {
      ((faculty.stats && faculty.stats.items) || []).forEach(item => {
        const natija = Number(item.natija);

        if (!aggregatedMap[natija]) {
          return;
        }

        aggregatedMap[natija].count += Number(item.count || 0);
      });
    });

    const items = sortByDisplayOrder(Object.values(aggregatedMap));
    const total = items.reduce((sum, item) => sum + Number(item.count || 0), 0);

    return {
      total,
      items: items.map(item => ({
        ...item,
        percent: total > 0 ? Number(((Number(item.count || 0) / total) * 100).toFixed(1)) : 0
      }))
    };
  }, [faculties, profiles]);

  const groupStats = useMemo(() => sortByDisplayOrder((groupData && groupData.stats && groupData.stats.items) || []), [groupData]);

  const groupTotalResults = useMemo(() => {
    const stats = (groupData && groupData.stats) || {};
    return Number(stats.total_results || stats.total || 0);
  }, [groupData]);

  const filteredStudents = useMemo(() => {
    const students = (groupData && groupData.students) || [];
    const search = studentSearch.trim().toLowerCase();

    if (!search) {
      return students;
    }

    return students.filter(student => {
      const haystack = [
        student.full_name,
        student.group,
        student.result,
        student.code,
        student.title,
        student.summary,
        String(student.talaba_id || '')
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [groupData, studentSearch]);

  const columns = useMemo(
    () => [
      {
        dataField: 'order',
        text: 'T/R',
        sort: true,
        classes: 'align-middle font-weight-semi-bold'
      },
      {
        dataField: 'full_name',
        text: 'F.I.O',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div className="psi-suicide-student">
            <div className="psi-suicide-student__avatar">{row.initials}</div>
            <div className="psi-suicide-student__copy">
              <strong>{value}</strong>
              <span>Talaba ID: {row.talaba_id || '—'}</span>
            </div>
          </div>
        )
      },
      {
        dataField: 'group',
        text: 'Guruh',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-suicide-group-pill">{value}</span>
      },
      {
        dataField: 'result',
        text: 'Kasbiy yo‘nalish',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => {
          if (row.is_unrated) {
            return (
              <Badge pill className={resultBadgeClass(row.tone)}>
                {unratedMeta.label}
              </Badge>
            );
          }

          return (
            <div className="psi-career-result-cell">
              <Badge pill className={resultBadgeClass(row.tone)}>
                {row.code}
              </Badge>
              <div className="psi-career-result-note">
                <strong>{row.title}</strong>
                <span>{value}</span>
              </div>
            </div>
          );
        }
      }
    ],
    [unratedMeta]
  );

  const renderProfileCard = (item, key) => (
    <Card key={key} className={`border-0 shadow-sm h-100 psi-career-profile psi-career-profile--${item.tone || 'secondary'}`}>
      <CardBody>
        <div className="psi-career-profile__head">
          <div className="psi-career-profile__mark">{item.code}</div>
          <button type="button" className="psi-career-profile__info" onClick={() => setActiveProfile(item)}>
            i
          </button>
        </div>
        <div className="psi-career-profile__value">{item.count}</div>
        <div className="psi-career-profile__title">{item.title}</div>
        <div className="psi-career-profile__label">{item.label}</div>
        <div className="psi-career-profile__progress">
          <span style={{ width: `${Math.max(0, Math.min(100, Number(item.percent || 0)))}%` }} />
        </div>
        <div className="psi-career-profile__meta">
          <span>{item.percent}%</span>
          <strong>{item.count} ta</strong>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <>
      <div className="psi-suicide-heading mb-3">
        <div>
          <div className="psi-suicide-heading__nav">Tahlillar / Kasb testi</div>
          <h3 className="mb-1">Kasb testi</h3>
          <p className="mb-0">Eski `p-admin/kasb.php` oqimiga mos ravishda fakultet, guruh va talaba kesimidagi kasbiy yo‘nalishlarni ko‘ring.</p>
        </div>
        {activePeriodLabel || groupId ? (
          <div className="psi-heading-badges">
            {activePeriodLabel ? <Badge pill className="psi-active-period-badge">{activePeriodLabel}</Badge> : null}
            {groupId ? (
              <Badge pill className="psi-suicide-heading__badge">
                {groupId}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <div className="psi-suicide-toolbar mb-3">
        <div className="psi-suicide-toolbar__copy">
          <h5 className="mb-1">{groupId ? `${groupId} guruhi` : 'Kasb testi bo‘yicha guruhlar'}</h5>
          <p className="text-600 mb-0">
            {groupId
              ? 'Tanlangan guruhda barcha talabalar ko‘rsatiladi, natija yo‘q bo‘lsa ham.'
              : 'Fakultetni ochib kerakli guruhni tanlang. 5 ta kasbiy tip bo‘yicha statistika va talaba ro‘yxati shu yerda chiqadi.'}
          </p>
        </div>

        <div className="psi-suicide-toolbar__controls">
          <Input
            value={groupSearch}
            onChange={({ target }) => setGroupSearch(target.value)}
            placeholder="Fakultet yoki guruh qidirish"
            className="psi-suicide-search"
          />
        </div>
      </div>

      <AnalysisSegmentCharts segments={segments} />

      {notFoundMessage ? (
        <Alert color="warning" className="mb-3">
          {notFoundMessage}
        </Alert>
      ) : null}

      <Row className="mb-3 psi-suicide-layout">
        <Col xl="5" className="mb-3 mb-xl-0">
          <Card className="border-0 shadow-sm psi-suicide-sidebar-card">
            <CardBody className="psi-suicide-sidebar-card__body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Fakultetlar va guruhlar</h5>
                {loadingFaculties ? <Spinner size="sm" color="primary" /> : <Badge color="light">{filteredFaculties.length} ta</Badge>}
              </div>

              <div className="psi-suicide-faculty-list">
                {loadingFaculties ? (
                  <div className="text-center text-500 py-4">Kasb testi ma`lumotlari yuklanmoqda...</div>
                ) : filteredFaculties.length > 0 ? (
                  filteredFaculties.map(faculty => {
                    const facultyKey = getFacultyKey(faculty);
                    const isOpen = !!expandedFaculties[facultyKey];
                    const facultyStats = sortByDisplayOrder((faculty.stats && faculty.stats.items) || []);

                    return (
                      <div key={facultyKey} className="psi-suicide-faculty">
                        <button
                          type="button"
                          className={`psi-suicide-faculty__header ${isOpen ? 'is-open' : ''}`}
                          onClick={() => toggleFaculty(facultyKey)}
                        >
                          <div className="psi-suicide-faculty__copy">
                            <strong>{faculty.name}</strong>
                            <span>
                              {faculty.group_count || (faculty.groups || []).length} ta guruh • {faculty.student_count} talaba
                            </span>
                          </div>
                          <span className="psi-suicide-faculty__toggle" aria-hidden="true">
                            {isOpen ? '−' : '+'}
                          </span>
                        </button>

                        {isOpen ? (
                          <div className="psi-suicide-faculty__body">
                            <div className="psi-suicide-mini-stats">
                              {facultyStats.map(item => (
                                <div key={`${facultyKey}-${item.natija}`} className={`psi-suicide-mini-stat psi-suicide-mini-stat--${item.tone}`}>
                                  <div className="psi-suicide-mini-stat__mark">{item.code}</div>
                                  <div className="psi-suicide-mini-stat__copy">
                                    <strong>{item.count}</strong>
                                    <span>{item.title}</span>
                                  </div>
                                  <small>{item.percent}%</small>
                                </div>
                              ))}
                            </div>

                            <div className="psi-suicide-group-grid">
                              {(faculty.groups || []).map(group => (
                                <button
                                  key={group.name}
                                  type="button"
                                  className={`psi-suicide-group-button ${groupId === group.name ? 'is-active' : ''}`}
                                  onClick={() => changeGroup(group.name)}
                                >
                                  <span>{group.name}</span>
                                  <small>{Number(group.result_count || 0)}/{Number(group.student_count || 0)}</small>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-500 py-4">Mos fakultet yoki guruh topilmadi.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl="7" className="psi-suicide-main-col">
          {loadingGroup ? (
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex align-items-center justify-content-center py-6">
                <div className="text-center">
                  <Spinner color="primary" className="mb-3" />
                  <div className="text-600">Guruh ma`lumotlari yuklanmoqda...</div>
                </div>
              </CardBody>
            </Card>
          ) : groupData ? (
            <div className="psi-suicide-main-shell">
              <Row className="mb-3">
                {groupStats.map(item => (
                  <Col md="6" xl={item.column || 4} key={`${groupData.group}-${item.natija}`} className="mb-3">
                    {renderProfileCard(item, `group-${groupData.group}-${item.natija}`)}
                  </Col>
                ))}
              </Row>

              <Card className="border-0 shadow-sm psi-suicide-main-card">
                <CardBody className="psi-suicide-main-card__body">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                    <div className="mb-3 mb-lg-0">
                      <h5 className="mb-1">{groupData.group} guruhi kesimida</h5>
                      <p className="text-600 mb-0">
                        {groupData.faculty} • {groupData.student_count} ta talaba • {groupTotalResults} ta baholangan natija
                      </p>
                    </div>
                    <div className="d-flex flex-column flex-sm-row">
                      <Input
                        value={studentSearch}
                        onChange={({ target }) => setStudentSearch(target.value)}
                        placeholder="Talaba qidirish"
                        className="psi-suicide-search"
                      />
                    </div>
                  </div>

                  {groupTotalResults === 0 ? (
                    <Alert color="info" className="mb-3">
                      Bu guruhda kasb testi hali baholanmagan. Jadvalda barcha talabalar Qiymatlanmagan holatida ko‘rsatiladi.
                    </Alert>
                  ) : null}

                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                    <Badge color="light" pill className="px-3 py-2">
                      {filteredStudents.length} ta yozuv
                    </Badge>
                    <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mt-2 mt-sm-0" />
                  </div>

                  <div className="psi-suicide-table-scroll">
                    <MedDataTable
                      data={filteredStudents}
                      columns={columns}
                      keyField="id"
                      pageSize={pageSize}
                      paginationResetKey={`${groupData.group}-${studentSearch}`}
                      noDataIndication={<div className="text-center py-4 text-500">Bu guruh bo‘yicha talaba topilmadi.</div>}
                      tableClasses="table table-hover fs--1 mb-0 med-dashboard-table psi-suicide-table"
                      headerClasses="bg-200 text-900"
                      wrapperClasses="table-responsive psi-suicide-table-wrap"
                    />
                  </div>
                </CardBody>
              </Card>
            </div>
          ) : (
            <Card className="border-0 shadow-sm h-100 psi-suicide-overview-card">
              <CardBody className="psi-suicide-overview-card__body">
                <div className="psi-suicide-overview-card__hero">
                  <div>
                    <div className="psi-suicide-overview-card__eyebrow">Kasb testi taqsimoti</div>
                    <h4 className="mb-1">Fakultetdan guruh tanlang</h4>
                    <p className="text-600 mb-0">
                      Eski PHP sahifadagi kabi 5 ta kasbiy tip bo‘yicha umumiy ko‘rinish shu yerda chiqadi. Har bir kartadagi i tugmasi batafsil talqinni ochadi.
                    </p>
                  </div>
                  <Badge color="light" pill className="px-3 py-2">
                    {overviewStats.total} ta baholangan natija
                  </Badge>
                </div>

                <Row className="mt-1">
                  {overviewStats.items.map(item => (
                    <Col md="6" xl={item.column || 4} key={`overview-${item.natija}`} className="mb-3">
                      {renderProfileCard(item, `overview-${item.natija}`)}
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>

      <Modal isOpen={!!activeProfile} toggle={() => setActiveProfile(null)} centered className="psi-career-modal">
        <ModalHeader toggle={() => setActiveProfile(null)}>{activeProfile ? activeProfile.modal_title : ''}</ModalHeader>
        <ModalBody>
          {activeProfile ? (
            <div className="psi-career-modal__content">
              <div className={`psi-career-modal__pill psi-career-modal__pill--${activeProfile.tone || 'secondary'}`}>
                {activeProfile.code}
              </div>
              <h5 className="mb-1">{activeProfile.title}</h5>
              <p className="mb-3">{activeProfile.description}</p>
              <div className="psi-career-modal__section-title">Mos kasblar</div>
              <p className="mb-0">{activeProfile.label}</p>
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setActiveProfile(null)}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default KasbAnalysis;
